import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { KanbanSquare, Sparkles, ListChecks, GitBranch, ChevronLeft, Plus, Trash2, ArrowUp, ArrowDown, Award, History } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import SettingsTile from "@/components/SettingsTile";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api from "@/lib/api";
import { toast } from "sonner";
import { confirmDialog } from "@/lib/dialogs";
import { useAuth } from "@/context/AuthContext";

const TILES = [
  { key: "touchpoints", title: "Touchpoints", desc: "Suggested actions & outcomes per stage", accent: "fuchsia", Icon: Sparkles },
  { key: "sources", title: "Lead Sources", desc: "Sources & discovery outcomes lists", accent: "indigo", Icon: ListChecks },
  { key: "stages", title: "Manage Stages", desc: "Pipeline columns before Won / Lost", accent: "teal", Icon: GitBranch },
  { key: "reasons", title: "Win / Loss Reasons", desc: "Reasons recorded when closing an opportunity", accent: "emerald", Icon: Award },
  { key: "cleanup", title: "Bulk Delete Log", desc: "Who cleared pipeline opportunities, and when", accent: "rose", Icon: History },
];

const META = {
  touchpoints: { title: "Touchpoints", accent: "fuchsia", Icon: Sparkles },
  sources: { title: "Lead Sources & Outcomes", accent: "indigo", Icon: ListChecks },
  stages: { title: "Manage Stages", accent: "teal", Icon: GitBranch },
  reasons: { title: "Win / Loss Reasons", accent: "emerald", Icon: Award },
  cleanup: { title: "Bulk Delete Log", accent: "rose", Icon: History },
};

export default function SettingsPipeline() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "super_admin";
  const [sp, setSp] = useSearchParams();
  const section = sp.get("section");
  const tiles = TILES.filter((t) => t.key !== "cleanup" || isAdmin);

  if (!section) {
    return (
      <div>
        <PageHeader title="Sales Pipeline Settings" subtitle="Configure touchpoints, lead sources and pipeline stages." accent="fuchsia" Icon={KanbanSquare}
          actions={<Button variant="outline" onClick={() => navigate("/admin")} data-testid="pipeline-settings-back"><ChevronLeft className="w-4 h-4 mr-1" />Settings</Button>} />
        <div className="p-4 md:p-8 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 max-w-[1200px]" data-testid="pipeline-settings-hub">
          {tiles.map((t) => <SettingsTile key={t.key} {...t} onClick={() => setSp({ section: t.key })} />)}
        </div>
      </div>
    );
  }

  const meta = META[section] || META.touchpoints;
  return (
    <div>
      <PageHeader title={meta.title} accent={meta.accent} Icon={meta.Icon}
        actions={<Button variant="outline" onClick={() => setSp({})} data-testid="pipeline-settings-section-back"><ChevronLeft className="w-4 h-4 mr-1" />Sales Pipeline Settings</Button>} />
      <div className="p-4 md:p-8 max-w-[900px]">
        {section === "touchpoints" && <TouchpointsManager />}
        {section === "sources" && <SourcesManager />}
        {section === "stages" && <StagesManager />}
        {section === "reasons" && <ReasonsManager />}
        {section === "cleanup" && (isAdmin ? <DeleteAuditManager /> : <p className="text-sm text-cass-slate" data-testid="audit-no-access">You need administrator access to view the bulk delete log.</p>)}
      </div>
    </div>
  );
}

function TouchpointsManager() {
  const [rows, setRows] = useState([]);
  const [stages, setStages] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => { api.get("/pipeline/meta").then((r) => { setStages(r.data.open_stages || []); setRows((r.data.touchpoints || []).map((t) => ({ ...t, outcomes: (t.outcomes || []).join(", ") }))); setLoaded(true); }); }, []);
  const upd = (i, patch) => setRows((rs) => rs.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const save = async () => {
    const clean = rows.filter((r) => (r.title || "").trim() && (r.stage || "").trim())
      .map((r) => ({ id: r.id, stage: r.stage, title: r.title, description: r.description || "", type: r.type || "suggested", timing_days: Number(r.timing_days) || 0, outcomes: String(r.outcomes || "").split(",").map((s) => s.trim()).filter(Boolean) }));
    setSaving(true);
    try { await api.put("/pipeline/touchpoints", { touchpoints: clean }); toast.success("Touchpoints saved"); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
    finally { setSaving(false); }
  };
  return (
    <div className="space-y-3" data-testid="touchpoints-manager">
      <p className="text-sm text-cass-slate">Suggested actions the team can log against an opportunity at each stage. Automated sending stays off — CASS keeps control of client contact.</p>
      <div className="space-y-2">
        {rows.map((r, i) => (
          <div key={i} className="border border-border rounded-lg p-3 space-y-1.5 bg-white" data-testid={`tp-row-${i}`}>
            <div className="flex items-center gap-2">
              <Select value={r.stage || ""} onValueChange={(v) => upd(i, { stage: v })}>
                <SelectTrigger className="w-48 h-8 text-xs" data-testid={`tp-stage-${i}`}><SelectValue placeholder="Stage" /></SelectTrigger>
                <SelectContent>{stages.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}</SelectContent>
              </Select>
              <Input value={r.title} onChange={(e) => upd(i, { title: e.target.value })} placeholder="Title" className="h-8 text-sm" data-testid={`tp-title-${i}`} />
              <Select value={r.type || "suggested"} onValueChange={(v) => upd(i, { type: v })}>
                <SelectTrigger className="w-28 h-8 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="suggested">Suggested</SelectItem><SelectItem value="manual">Manual</SelectItem></SelectContent>
              </Select>
              <Input type="number" value={r.timing_days} onChange={(e) => upd(i, { timing_days: e.target.value })} placeholder="+d" className="h-8 w-16 text-sm" title="Timing (days)" />
              <button onClick={() => setRows(rows.filter((_, j) => j !== i))} className="text-red-400 hover:text-red-600" title="Remove"><Trash2 className="w-4 h-4" /></button>
            </div>
            <Input value={r.description} onChange={(e) => upd(i, { description: e.target.value })} placeholder="Description" className="h-8 text-xs" data-testid={`tp-desc-${i}`} />
            <Input value={r.outcomes} onChange={(e) => upd(i, { outcomes: e.target.value })} placeholder="Outcomes, comma separated" className="h-8 text-xs" data-testid={`tp-outcomes-${i}`} />
          </div>
        ))}
      </div>
      <Button size="sm" variant="outline" onClick={() => setRows([...rows, { stage: stages[0]?.key || "", title: "", description: "", type: "suggested", timing_days: 0, outcomes: "" }])} data-testid="tp-add-row"><Plus className="w-4 h-4 mr-1" />Add touchpoint</Button>
      <div><Button className="bg-cass-navy text-white" onClick={save} disabled={saving || !loaded} data-testid="tp-save-btn">{saving ? "Saving…" : "Save touchpoints"}</Button></div>
    </div>
  );
}

function SourcesManager() {
  const [sources, setSources] = useState([]);
  const [outcomes, setOutcomes] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => { api.get("/pipeline/meta").then((r) => { setSources(r.data.lead_sources || []); setOutcomes(r.data.discovery_outcomes || []); setLoaded(true); }); }, []);
  const save = async () => {
    const s = sources.map((x) => (x || "").trim()).filter(Boolean);
    const o = outcomes.map((x) => (x || "").trim()).filter(Boolean);
    if (!s.length) { toast.error("Add at least one lead source"); return; }
    if (!o.length) { toast.error("Add at least one discovery outcome"); return; }
    setSaving(true);
    try { await api.put("/pipeline/lead-sources", { lead_sources: s, discovery_outcomes: o }); toast.success("Lists saved"); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
    finally { setSaving(false); }
  };
  const col = (label, items, setItems, tid) => (
    <div className="flex-1">
      <div className="text-sm font-semibold text-cass-navy mb-2">{label}</div>
      <div className="space-y-1">
        {items.map((v, i) => (
          <div key={i} className="flex items-center gap-1">
            <Input value={v} onChange={(e) => setItems(items.map((x, j) => (j === i ? e.target.value : x)))} data-testid={`${tid}-${i}`} />
            <button onClick={() => setItems(items.filter((_, j) => j !== i))} className="text-red-400 hover:text-red-600" title="Remove"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
      <Button size="sm" variant="outline" className="mt-2" onClick={() => setItems([...items, ""])} data-testid={`${tid}-add`}><Plus className="w-4 h-4 mr-1" />Add</Button>
    </div>
  );
  return (
    <div className="space-y-4" data-testid="sources-manager">
      <p className="text-sm text-cass-slate">Configurable lists used across the sales pipeline. Lead source is kept separate from the Xero accounting sales source.</p>
      <div className="flex flex-col sm:flex-row gap-6">
        {col("Lead sources", sources, setSources, "source-row")}
        {col("Discovery outcomes", outcomes, setOutcomes, "outcome-row")}
      </div>
      <div><Button className="bg-cass-navy text-white" onClick={save} disabled={saving || !loaded} data-testid="sources-save-btn">{saving ? "Saving…" : "Save lists"}</Button></div>
    </div>
  );
}

function ReasonsManager() {
  const [win, setWin] = useState([]);
  const [loss, setLoss] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => { api.get("/pipeline/meta").then((r) => { setWin(r.data.win_reasons || []); setLoss(r.data.loss_reasons || []); setLoaded(true); }); }, []);
  const save = async () => {
    const w = win.map((x) => (x || "").trim()).filter(Boolean);
    const l = loss.map((x) => (x || "").trim()).filter(Boolean);
    if (!w.length || !l.length) { toast.error("Add at least one win and one loss reason"); return; }
    setSaving(true);
    try { await api.put("/pipeline/reasons", { win_reasons: w, loss_reasons: l }); toast.success("Reasons saved"); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
    finally { setSaving(false); }
  };
  const col = (label, items, setItems, tid) => (
    <div className="flex-1">
      <div className="text-sm font-semibold text-cass-navy mb-2">{label}</div>
      <div className="space-y-1">
        {items.map((v, i) => (
          <div key={i} className="flex items-center gap-1">
            <Input value={v} onChange={(e) => setItems(items.map((x, j) => (j === i ? e.target.value : x)))} data-testid={`${tid}-${i}`} />
            <button onClick={() => setItems(items.filter((_, j) => j !== i))} className="text-red-400 hover:text-red-600" title="Remove"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
      <Button size="sm" variant="outline" className="mt-2" onClick={() => setItems([...items, ""])} data-testid={`${tid}-add`}><Plus className="w-4 h-4 mr-1" />Add</Button>
    </div>
  );
  return (
    <div className="space-y-4" data-testid="reasons-manager">
      <p className="text-sm text-cass-slate">These options appear when an opportunity is marked Won or Lost on the Sales Pipeline.</p>
      <div className="flex flex-col sm:flex-row gap-6">
        {col("Won reasons", win, setWin, "win-reason")}
        {col("Lost reasons", loss, setLoss, "loss-reason")}
      </div>
      <div><Button className="bg-cass-navy text-white" onClick={save} disabled={saving || !loaded} data-testid="save-pipeline-reasons">{saving ? "Saving…" : "Save reasons"}</Button></div>
    </div>
  );
}

function StagesManager() {
  const [rows, setRows] = useState([]);
  const [orig, setOrig] = useState([]);
  const [leads, setLeads] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    api.get("/pipeline/meta").then((r) => { const st = (r.data.open_stages || []).map((s) => ({ key: s.key, label: s.label })); setRows(st); setOrig(st); setLoaded(true); });
    api.get("/pipeline/leads").then((r) => setLeads(r.data || [])).catch(() => {});
  }, []);
  const move = (i, d) => setRows((rs) => { const a = [...rs]; const j = i + d; if (j < 0 || j >= a.length) return rs; [a[i], a[j]] = [a[j], a[i]]; return a; });
  const save = async () => {
    const clean = rows.filter((r) => (r.label || "").trim());
    if (!clean.length) { toast.error("Add at least one stage"); return; }
    const kept = new Set(clean.map((r) => r.key).filter(Boolean));
    const removed = orig.filter((s) => !kept.has(s.key));
    const affected = leads.filter((l) => removed.some((s) => s.key === l.stage));
    if (affected.length) {
      const ok = await confirmDialog({ title: "Move opportunities?", description: `${affected.length} opportunit${affected.length === 1 ? "y" : "ies"} in ${removed.map((s) => s.label).join(", ")} will move to ${clean[0].label}. Continue?`, confirmText: "Move & save", tone: "destructive" });
      if (!ok) return;
    }
    setSaving(true);
    try { await api.put("/pipeline/config", { stages: clean }); toast.success("Stages saved"); const st = clean.map((r) => ({ key: r.key, label: r.label })); setOrig(st); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
    finally { setSaving(false); }
  };
  return (
    <div className="space-y-3" data-testid="stages-manager">
      <p className="text-sm text-cass-slate">Won and Lost are always the last columns. Deleting a stage moves its opportunities to the first stage.</p>
      <div className="space-y-2">
        {rows.map((r, i) => (
          <div key={i} className="flex items-center gap-2">
            <Input value={r.label} onChange={(e) => setRows(rows.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))} placeholder="Stage name" data-testid={`stage-row-${i}`} />
            <button disabled={i === 0} onClick={() => move(i, -1)} className="text-slate-400 hover:text-cass-navy disabled:opacity-30" title="Move up"><ArrowUp className="w-4 h-4" /></button>
            <button disabled={i === rows.length - 1} onClick={() => move(i, 1)} className="text-slate-400 hover:text-cass-navy disabled:opacity-30" title="Move down"><ArrowDown className="w-4 h-4" /></button>
            <button disabled={rows.length <= 1} onClick={() => setRows(rows.filter((_, j) => j !== i))} className="text-red-400 hover:text-red-600 disabled:opacity-30" title="Delete stage"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
      <Button size="sm" variant="outline" onClick={() => setRows([...rows, { label: "" }])} data-testid="stage-add-row"><Plus className="w-4 h-4 mr-1" />Add stage</Button>
      <div><Button className="bg-cass-navy text-white" onClick={save} disabled={saving || !loaded} data-testid="stage-save-btn">{saving ? "Saving…" : "Save stages"}</Button></div>
    </div>
  );
}

function DeleteAuditManager() {
  const [rows, setRows] = useState([]);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => { api.get("/pipeline/delete-audit").then((r) => setRows(r.data || [])).catch(() => {}).finally(() => setLoaded(true)); }, []);
  const fmt = (iso) => { if (!iso) return ""; const d = new Date(iso); return isNaN(d.getTime()) ? iso : d.toLocaleString("en-GB", { day: "2-digit", month: "short", year: "2-digit", hour: "2-digit", minute: "2-digit" }); };
  return (
    <div className="space-y-3" data-testid="delete-audit-manager">
      <p className="text-sm text-cass-slate">A record of every admin bulk delete on the Sales Pipeline — who ran it, when, how many opportunities, and whether it was later undone.</p>
      {loaded && rows.length === 0 && <p className="text-sm text-cass-slate" data-testid="audit-empty">No bulk deletes recorded yet.</p>}
      <div className="space-y-2">
        {rows.map((r) => (
          <div key={r.id} className="border border-border rounded-lg p-3 bg-white" data-testid={`audit-row-${r.id}`}>
            <div className="flex items-center justify-between gap-2">
              <div className="text-sm font-semibold text-cass-navy">{r.method === "empty_stage" ? `Emptied “${r.stage_label || r.stage}”` : "Deleted selected"} · {r.count} opportunit{r.count === 1 ? "y" : "ies"}</div>
              {r.restored && <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200" data-testid={`audit-undone-${r.id}`}>Undone</span>}
            </div>
            <div className="text-xs text-cass-slate mt-0.5">{r.by || "—"} · {fmt(r.at)}{r.business_count ? ` · ${r.business_count} prospect ${r.business_count === 1 ? "business" : "businesses"} removed` : ""}</div>
            {(r.companies || []).length > 0 && <div className="text-xs text-cass-slate mt-1 truncate" title={(r.companies || []).join(", ")}>{(r.companies || []).slice(0, 8).join(", ")}{(r.companies || []).length > 8 ? ` +${r.companies.length - 8} more` : ""}</div>}
            {r.restored && <div className="text-[11px] text-emerald-700 mt-1">Undone by {r.restored_by || "—"} · {fmt(r.restored_at)}</div>}
          </div>
        ))}
      </div>
    </div>
  );
}
